/**
 * 桥梁管理 - 主逻辑文件
 * 整合地图控制、桥梁信息和区域管理等功能模块
 */
import BridgeMapController from './controllers/BridgeMapController';
import BridgeFormController from './controllers/BridgeFormController';
import BridgeAreaController from './controllers/BridgeAreaController';
import BridgeEventBus, { BRIDGE_EVENTS } from '@/utils/BridgeEventBus';
import bridgeMapStore from '@/store/bridgeMapStore';
import mapUtils from '@/utils/mapUtils';

export default {
  name: 'BridgeManagement',
  
  data() {
    // 从各个控制器合并数据
    return {
      // 通用状态
      loading: false,
      submitting: false,
      activeTab: 'info', // 当前活动的标签页：info, area, blacklist
      infoPanelCollapsed: false, // 信息面板折叠状态
      areaPanelCollapsed: false, // 区域面板折叠状态
      blacklistPanelCollapsed: false, // 船舶黑名单面板折叠状态
      currentBridgeId: null, // 当前桥梁ID
      selectedArea: null, // 当前选中的区域
      
      // 从BridgeMapController获取地图相关数据
      ...BridgeMapController.data(),
      
      // 从BridgeFormController获取桥梁表单相关数据
      ...BridgeFormController.data(),
      
      // 从BridgeAreaController获取区域管理相关数据
      // 注意：移除了drawingCoordinates，改为从bridgeMapStore获取
      ...(() => {
        const areaData = BridgeAreaController.data();
        // 删除本地的drawingCoordinates，将使用计算属性
        delete areaData.drawingCoordinates;
        return areaData;
      })(),
    };
  },
  
  computed: {
    hasCoordinates() {
      return !!this.bridgeForm.longitude && !!this.bridgeForm.latitude;
    },
    
    // 添加计算属性，从store获取位置拾取状态
    isLocationPickingActive() {
      return bridgeMapStore.state.isLocationPicking;
    },
    
    // 从store获取绘制坐标数组
    drawingCoordinates: {
      get() {
        return bridgeMapStore.state.drawingCoordinates;
      },
      set(value) {
        // 由于这是数组，直接赋值会丢失响应式，使用Vue.set或简单的替换操作
        bridgeMapStore.state.drawingCoordinates = [...value];
      }
    },
    
    // 从store获取绘制状态
    isDrawingActive: {
      get() {
        return bridgeMapStore.state.isDrawing;
      },
      set(value) {
        bridgeMapStore.actions.startDrawing(value);
      }
    }
  },
  
  watch: {
    // 监视drawingCoordinates变化，当长度大于等于2时，自动设置编辑状态
    'drawingCoordinates.length': function(newLength) {
      if (newLength >= 2 && !this.isEditingArea && this.isDrawingActive) {
        this.isEditingArea = true;
      }
    }
  },
  
  created() {
    try {
      // 重要：首先重置所有状态，确保每次进入页面时都是干净的状态
      bridgeMapStore.actions.resetAllState();
      
      console.log('桥梁管理页面初始化...');
      // 先初始化必要的对象，避免后续出现undefined错误
      if (!this.areaForm) {
        this.areaForm = {
          id: null,
          areaName: '',
          areaCode: '',
          areaType: '',
          bridgeId: null,
          coordinates: '',
          description: '',
          direction: 0,
          isEnabled: 1
        };
      }
      
      // 确保bridgeForm已初始化
      if (!this.bridgeForm) {
        this.bridgeForm = {
          id: null,
          bridgeName: '',
          bridgeCode: '',
          bridgeType: '',
          longitude: '',
          latitude: '',
          buildTime: '',
          structureInfo: '',
          clearanceHeight: '',
          warningHeight: '',
          warningSpeed: '',
          status: 1
        };
      }
      
      // 初始化区域类型数据，确保组件能够正常工作
      if (!this.areaTypeOptions) {
        this.areaTypeOptions = [];
        this.areaTypeNameMap = {};
      }
      
      // 初始化地图
      this.initMap();
      
      // 加载区域类型字典数据
      this.loadAreaTypeOptions();
      
      // 检查是否为编辑模式
      const bridgeId = this.$route.query.id;
      if (bridgeId) {
        console.log('编辑模式：桥梁ID =', bridgeId);
        this.isEdit = true;
        this.currentBridgeId = bridgeId;
        
        // 尝试从缓存获取桥梁位置信息
        const cachedPosition = bridgeMapStore.actions.getBridgePositionFromCache(bridgeId);
        
        // 如果缓存中有位置信息，预先设置到bridgeForm中
        if (cachedPosition && cachedPosition.latitude && cachedPosition.longitude) {
          console.log(`已从缓存获取桥梁[${bridgeId}]位置信息，准备初始化地图`);
          this.bridgeForm.latitude = cachedPosition.latitude;
          this.bridgeForm.longitude = cachedPosition.longitude;
          this.marker = [cachedPosition.latitude, cachedPosition.longitude];
          
          // 通知地图控制器更新中心点
          if (this.map) {
            console.log('使用缓存位置设置地图中心:', this.marker);
            this.map.setView(this.marker, 15);
          }
        }
        
        // 使用Promise链确保数据加载顺序
        this.fetchBridgeDetail(bridgeId)
          .then(() => {
            // 桥梁数据加载完成后，加载区域数据
            if (this.bridgeForm && this.bridgeForm.id) {
              console.log('编辑模式：加载桥梁区域数据');
              return this.loadBridgeAreas(this.bridgeForm.id);
            }
          })
          .catch(err => {
            console.error('加载桥梁及其区域数据失败:', err);
          });
      } else {
        console.log('创建模式：新建桥梁');
        // 生成新的桥梁编号
        this.generateBridgeCode();
        
        // 只有非编辑模式才加载所有桥梁数据
        this.loadAllBridges();
      }
      
      // 注册事件监听
      this.setupEventListeners();
    } catch (error) {
      console.error('BridgeManagement初始化错误:', error);
    }
  },
  
  mounted() {
    // 确保初始布局完成后更新地图尺寸
    this.$nextTick(() => {
      if (this.map) {
        this.map.invalidateSize();
      }
    });
    
    // 添加窗口大小变化监听
    window.addEventListener('resize', this.handleWindowResize);
    
    // 监听区域面板中的点图标选择事件
    if (this.$refs.areaPanel) {
      this.$refs.areaPanel.$on('select-point-icon', this.handlePointIconSelected);
    }
  },
  
  updated() {
    // 每次更新后尝试更新地图尺寸
    this.$nextTick(() => {
      if (this.map) {
        this.map.invalidateSize();
      }
    });
  },
  
  beforeDestroy() {
    console.log('桥梁管理页面销毁，清理资源...');
    
    // 移除窗口大小变化监听
    window.removeEventListener('resize', this.handleWindowResize);
    
    // 注销事件监听
    this.removeEventListeners();
    
    // 清除store中的状态
    if (bridgeMapStore && bridgeMapStore.actions) {
      bridgeMapStore.actions.resetAllState();
    }
    
    // 清除组件状态
    this.selectedArea = null;
    this.isEditingArea = false;
  },
  
  methods: {
    // 从各控制器合并方法
    ...BridgeMapController.methods,
    ...BridgeFormController.methods,
    ...BridgeAreaController.methods,
    
    // 【重要覆盖】重写saveArea方法，确保编辑锁定状态清理
    async saveArea(areaData) {
      console.log('=== BridgeManagement.saveArea 被调用 ===');
      console.log('传入数据:', areaData);
      console.log('保存前store状态:', {
        isEditingLocked: bridgeMapStore.getters.isEditingLocked(),
        editingAreaId: bridgeMapStore.getters.editingAreaId(),
        isEditingArea: bridgeMapStore.getters.isEditingArea()
      });
      
      // 调用原始的saveArea方法（来自BridgeAreaController）
      const result = await BridgeAreaController.methods.saveArea.call(this, areaData);
      
      // 【关键修复】如果保存成功，清理编辑锁定状态
      if (result) {
        console.log('saveArea保存成功，开始清理编辑锁定状态');
        bridgeMapStore.actions.endEditingArea();
        
        // 通知AreaPanel组件保存成功
        if (this.$refs.areaPanel && typeof this.$refs.areaPanel.handleSaveSuccess === 'function') {
          this.$refs.areaPanel.handleSaveSuccess();
        }
        
        console.log('saveArea保存后store状态:', {
          isEditingLocked: bridgeMapStore.getters.isEditingLocked(),
          editingAreaId: bridgeMapStore.getters.editingAreaId(),
          isEditingArea: bridgeMapStore.getters.isEditingArea()
        });
      } else {
        console.log('saveArea保存失败，不清理编辑状态');
      }
      
      console.log('===================================');
      return result;
    },
    
    // 处理标签页切换
    handleTabChange(tab) {
      this.activeTab = tab;
      
      // 如果切换到区域标签页，加载区域数据
      if (tab === 'area') {
        console.log('切换到区域标签页，执行相关数据加载...');
        
        // 重要：首先重置绘制状态，确保新的绘制从零开始
        this.isEditingArea = false;
        this.selectedArea = null;
        
        // 使用store方法重置绘制状态，drawingCoordinates现在是计算属性
        bridgeMapStore.actions.clearDrawing();
        // 确保绘制模式关闭
        if (bridgeMapStore.state.isDrawing) {
          bridgeMapStore.actions.startDrawing(false);
        }
        
        // 如果区域类型选项为空，尝试加载
        if (this.areaTypeOptions.length === 0) {
          console.log('区域类型选项为空，重新加载区域类型数据');
          this.loadAreaTypeOptions();
        } else {
          console.log('区域类型选项已存在，数量:', this.areaTypeOptions.length);
        }

        // 加载桥梁的区域数据
        if (this.bridgeForm && this.bridgeForm.id) {
          console.log('加载桥梁区域数据，桥梁ID:', this.bridgeForm.id);
          this.loadBridgeAreas(this.bridgeForm.id);
        } else {
          console.warn('桥梁ID不存在，无法加载区域数据');
        }
      }
      // 如果切换到船舶黑名单标签页，确保桥梁ID已设置
      else if (tab === 'blacklist') {
        // 确保当前桥梁ID有值
        if (this.bridgeForm && this.bridgeForm.id) {
          this.currentBridgeId = this.bridgeForm.id;
          console.log('切换到船舶黑名单标签页，桥梁ID:', this.currentBridgeId);
        } else {
          console.warn('桥梁ID不存在，船舶黑名单功能可能受限');
        }
      }
      // 如果切换到桥梁信息标签页，确保区域图层已加载
      else if (tab === 'info') {
        // 加载区域类型字典数据
        if (this.areaTypeOptions.length === 0) {
          this.loadAreaTypeOptions();
        }
        
        // 加载桥梁类型字典数据
        this.loadBridgeTypeOptions();
        
        // 确保桥梁区域数据已加载
        if (this.bridgeForm && this.bridgeForm.id && this.savedAreas.length === 0) {
          console.log('桥梁信息页加载区域数据，桥梁ID:', this.bridgeForm.id);
          this.loadBridgeAreas(this.bridgeForm.id);
        }
      }
    },
    

    

    
    // 显示区域警戒规则弹窗 - 直接在区域面板中处理
    showAreaAlertSettings(area) {
      // 设置选中的区域
      this.selectedArea = area;
      
      // 确保当前桥梁ID有值
      if (this.bridgeForm && this.bridgeForm.id) {
        this.currentBridgeId = this.bridgeForm.id;
      }
      
      console.log('显示区域警戒规则，区域:', area ? area.name || area.areaName : '未知', '桥梁ID:', this.currentBridgeId);
      
      // 通知区域面板显示警戒规则设置
      this.$nextTick(() => {
        if (this.$refs.areaPanel) {
          this.$refs.areaPanel.$emit('show-area-alert', area);
        }
      });
    },
    
    // 显示全局警戒设置
    showAlertSettings() {
      // 切换到区域管理标签页
      this.activeTab = 'area';
      console.log('显示全局警戒设置，已切换到区域管理标签');
      
      // 确保当前桥梁ID有值
      if (this.bridgeForm && this.bridgeForm.id) {
        this.currentBridgeId = this.bridgeForm.id;
      }
    },
    
    // 处理区域警戒规则弹窗关闭 - 已不再需要，但保留方法避免引用错误
    handleAreaAlertClose() {
      // 方法保留但内部逻辑已不需要
      console.log('警戒规则面板关闭');
    },
    
    // 处理信息面板折叠状态变化
    handleInfoPanelCollapse(collapsed) {
      this.infoPanelCollapsed = collapsed;
    },
    
    // 处理区域面板折叠状态变化
    handleAreaPanelCollapse(collapsed) {
      this.areaPanelCollapsed = collapsed;
    },
    
    // 处理黑名单面板折叠状态变化
    handleBlacklistPanelCollapse(collapsed) {
      this.blacklistPanelCollapsed = collapsed;
    },
    
    // 处理关闭黑名单面板
    handleCloseBlacklistPanel() {
      // 切换回桥梁信息标签页
      this.activeTab = 'info';
    },
    
    // 处理布局变化，通知地图组件更新大小
    handleLayoutChange() {
      if (this.map) {
        setTimeout(() => {
          this.map.invalidateSize();
        }, 300);
      }
    },
    
    // 注册事件监听
    setupEventListeners() {
      // 监听地图点击事件
      BridgeEventBus.$on(BRIDGE_EVENTS.MAP_CLICK, this.handleMapClick);
      
      // 监听区域选择事件
      BridgeEventBus.$on(BRIDGE_EVENTS.AREA_SELECTED, this.handleAreaSelected);
      
      // 监听位置拾取完成事件
      BridgeEventBus.$on(BRIDGE_EVENTS.LOCATION_PICKED, this.handleLocationPicked);
    },
    
    // 注销事件监听
    removeEventListeners() {
      BridgeEventBus.$off(BRIDGE_EVENTS.MAP_CLICK, this.handleMapClick);
      BridgeEventBus.$off(BRIDGE_EVENTS.AREA_SELECTED, this.handleAreaSelected);
      BridgeEventBus.$off(BRIDGE_EVENTS.LOCATION_PICKED, this.handleLocationPicked);
    },
    
    // 处理地图点击事件 - 使用统一store管理
    handleMapClick(latlng) {
      try {
        // 参数校验
        if (!latlng || typeof latlng.lat === 'undefined' || typeof latlng.lng === 'undefined') {
          console.warn('无效的地图点击坐标', latlng);
          return;
        }
        
        console.log('地图点击事件触发: ', {
          坐标: [latlng.lat, latlng.lng],
          标签页: this.activeTab,
          绘制模式: this.isDrawingActive,
          区域类型: this.areaForm ? this.areaForm.areaType : null,
          几何类型: this.areaForm ? this.areaForm.geometryType : null
        });
        
        // 根据当前模式处理点击事件
        if (bridgeMapStore.state.isLocationPicking) {
          console.log('位置拾取模式');
          // 桥梁位置标记模式 - 使用store方法
          bridgeMapStore.actions.endLocationPicking(latlng);
          
          // 同步本地状态
          this.handleLocationPicked(latlng);
        } else if (this.isDrawingActive && this.activeTab === 'area') {
          console.log('区域绘制模式');
          // 判断是否是点标注
          if (this.areaForm.geometryType === 'POINT') {
            console.log('点标注模式，准备调用handlePointMarkerClick');
            // 直接在点击位置添加点标注
            this.handlePointMarkerClick(latlng);
          } else {
            console.log('常规区域绘制模式');
            console.log('当前store中的markType:', bridgeMapStore.state.markType);
            console.log('当前几何类型:', this.areaForm.geometryType);
            
            // 确保markType与几何类型一致
            const expectedMarkType = this.getMarkTypeFromGeometryType(this.areaForm.geometryType);
            if (bridgeMapStore.state.markType !== expectedMarkType) {
              console.warn(`markType不匹配！当前: ${bridgeMapStore.state.markType}, 期望: ${expectedMarkType}`);
              // 重新设置正确的markType
              bridgeMapStore.state.markType = expectedMarkType;
              console.log('已修正markType为:', expectedMarkType);
            }
            
            // 其他区域绘制模式 - 使用store方法
            bridgeMapStore.actions.handleDrawingClick(latlng);
            
            // 不需要手动同步drawingCoordinates，因为现在它是计算属性
            // 只需同步编辑状态
            if (bridgeMapStore.state.isEditingArea) {
              this.isEditingArea = true;
            }
          }
        } else {
          console.log('未处于绘制模式或非区域标签页，点击事件忽略');
        }
      } catch (error) {
        console.error('处理地图点击事件失败:', error);
      }
    },
    
    // 处理点标注点击事件
    handlePointMarkerClick(latlng) {
      console.log('处理点标注点击事件', latlng);
      try {
        if (!this.$refs.areaPanel || !latlng) return;
        
        // 创建单点坐标数组
        const coordinates = [latlng.lat, latlng.lng];
        
        // 防止冲突：先清空bridgeMapStore中的绘制坐标
        bridgeMapStore.actions.clearDrawing();
        
        // 更新绘制坐标 - 重要：对于点标注，只需要一个点
        this.drawingCoordinates = [coordinates]; // 存储为二维数组的第一个元素
        
        // 【修复字段重置问题】：不直接修改areaForm，而是从表单获取数据
        console.log('点击地图创建点标注，使用表单数据');
        
        // 从区域面板获取当前表单数据，确保使用用户编辑的数据
        let formData = {};
        if (this.$refs.areaPanel && this.$refs.areaPanel.localAreaForm) {
          formData = this.$refs.areaPanel.localAreaForm;
          console.log('使用区域面板表单数据:', {
            name: formData.name,
            color: formData.color,
            icon: formData.icon,
            geometryType: formData.geometryType,
            areaType: formData.areaType
          });
        }
        
        // 只在必要时设置几何类型为POINT，但不修改其他字段
        if (this.areaForm && this.areaForm.geometryType !== 'POINT') {
          this.areaForm.geometryType = 'POINT';
          console.log('设置几何类型为POINT');
        }
        
        // 更新区域面板的点坐标
        if (this.$refs.areaPanel && typeof this.$refs.areaPanel.updatePointCoordinates === 'function') {
          this.$refs.areaPanel.updatePointCoordinates(coordinates);
        }
        
        // 【关键修复】：使用表单中的数据而不是areaForm中的数据
        const iconName = formData.icon || this.areaForm.icon || this.areaForm.iconName || 'el-icon-location';
        const pointName = formData.name || formData.areaName || this.areaForm.areaName || this.areaForm.name || '新标记点';
        const pointColor = formData.color || this.areaForm.color || '#FF409E';
        
        console.log('创建点标注使用的数据:', {
          coordinates: coordinates,
          iconName: iconName,
          name: pointName,
          color: pointColor
        });
        
        // 在地图上添加点标注
        if (this.$refs.bridgeMap && typeof this.$refs.bridgeMap.addPointMarker === 'function') {
          this.$refs.bridgeMap.addPointMarker({
            coordinates: coordinates,
            iconName: iconName,
            name: pointName,
            color: pointColor
          });
        }
        
        // 设置编辑状态
        this.isEditingArea = true;
        
        // 关闭绘制模式
        this.isDrawingActive = false;
        
        // 标记绘制已完成，使点可拖拽
        if (this.$refs.bridgeMap) {
          this.$refs.bridgeMap.drawingCompleted = true;
          // 不再直接设置currentAreaType，而是通过areaForm.areaType与.sync修饰符更新
        }
        
        console.log('点标注已添加:', coordinates, iconName);
      } catch (error) {
        console.error('处理点标注点击事件失败:', error);
      }
    },
    
    // 处理点标注图标选择
    handlePointIconSelected(iconName) {
      if (!this.$refs.bridgeMap || !iconName) return;
      
      // 确保是点标注模式
      if (this.areaForm.geometryType === 'POINT' && this.drawingCoordinates && this.drawingCoordinates.length > 0) {
        // 如果地图上已有点标注，则更新图标
        if (this.$refs.bridgeMap.pointMarkers && this.$refs.bridgeMap.pointMarkers.length > 0) {
          const pointMarker = this.$refs.bridgeMap.pointMarkers[0];
          pointMarker.iconName = iconName;
          console.log('从BridgeManagement更新点标注图标:', iconName);
        }
      }
    },
    
    // 提交表单 - 根据当前标签页决定提交桥梁信息或区域信息
    async submitForm() {
      if (this.submitting) return; // 防止重复提交
      
      try {
        if (this.activeTab === 'area') {
          // 区域标签页，直接尝试保存区域
          // 确保有绘制内容时设置编辑状态
          if (this.drawingCoordinates.length >= 3 && !this.isEditingArea) {
            console.log('检测到区域绘制内容，自动设置编辑状态');
            this.isEditingArea = true;
            
            // 确保区域类型已设置(使用枚举值)
            if (!this.areaForm.areaType && this.areaTypeOptions.length > 0) {
              this.areaForm.areaType = this.areaTypeOptions[0].value;
              console.log('设置区域类型枚举值:', this.areaForm.areaType);
            }
            
            // 自动根据区域类型设置区域名称
            if (this.areaForm.areaType && this.areaTypeNameMap[this.areaForm.areaType]) {
              const displayName = this.areaTypeNameMap[this.areaForm.areaType];
              this.areaForm.name = displayName;
              this.areaForm.areaName = displayName;
              console.log(`设置区域名称为: ${displayName} (枚举值: ${this.areaForm.areaType})`);
            } else if (!this.areaForm.name && !this.areaForm.areaName) {
              // 如果没有区域名称，使用默认名称
              this.areaForm.name = `区域_${new Date().toLocaleTimeString()}`;
              this.areaForm.areaName = this.areaForm.name;
              console.log('使用默认区域名称:', this.areaForm.name);
            }
            
            // 确保区域类型是枚举值而不是显示名称
            const validEnums = [
              'BRIDGE', 'BRIDGE_LINE', 'BRIDGE_MAST', 'CHANNEL', 
              'CHANNEL_DOWN', 'CHANNEL_UP', 'NAVIGATION', 'NAVIGATION_DOWN', 
              'NAVIGATION_UP', 'NO_NAVIGATION', 'OTHER', 'RADAR_AREA_DOWN', 
              'RADAR_AREA_UP', 'REPORT_LINE_DOWN', 'REPORT_LINE_UP', 
              'RESTRICTED', 'WARNING', 'WARNING_LINE_DOWN', 'WARNING_LINE_UP'
            ];
            
            // 检查区域类型是否为有效枚举值
            if (typeof this.areaForm.areaType === 'string' && !validEnums.includes(this.areaForm.areaType)) {
              // 如果不是有效枚举，尝试通过名称映射找到枚举值
              const foundEnum = Object.keys(this.areaTypeNameMap).find(
                key => this.areaTypeNameMap[key] === this.areaForm.areaType
              );
              
              if (foundEnum) {
                console.log(`区域类型不是有效枚举，已从 "${this.areaForm.areaType}" 修正为 "${foundEnum}"`);
                this.areaForm.areaType = foundEnum;
              } else {
                // 如果找不到映射，使用默认枚举值
                console.warn(`区域类型 "${this.areaForm.areaType}" 不是有效枚举且找不到映射，使用默认值 "RESTRICTED"`);
                this.areaForm.areaType = 'RESTRICTED';
              }
            }
          }
          
          console.log('提交区域表单:', JSON.stringify({
            areaName: this.areaForm.name,
            areaType: this.areaForm.areaType,
            coordinates: this.drawingCoordinates.length + '点'
          }));
          
          // 保存区域数据前备份当前选中的区域
          const previousSelectedArea = this.selectedArea;
          
          // 保存区域数据
          const success = await this.saveArea();
          
          // 如果保存成功，明确重置选中区域，避免下次绘制时包含上一个区域的点
          if (success) {
            console.log('区域保存成功，重置选中区域，之前的区域ID:', previousSelectedArea ? previousSelectedArea.id : 'none');
            
            // 【调试】submitForm中的保存成功状态清理诊断
            console.log('=== submitForm保存成功，开始状态清理 ===');
            console.log('保存前store状态:', {
              isEditingLocked: bridgeMapStore.getters.isEditingLocked(),
              editingAreaId: bridgeMapStore.getters.editingAreaId(),
              isEditingArea: bridgeMapStore.getters.isEditingArea()
            });
            
            // 【关键修复】先清理store的编辑状态
            bridgeMapStore.actions.endEditingArea();
            
            // 【修复】通知AreaPanel组件保存成功，同步清理状态
            if (this.$refs.areaPanel && typeof this.$refs.areaPanel.handleSaveSuccess === 'function') {
              this.$refs.areaPanel.handleSaveSuccess();
            }
            
            this.selectedArea = null;
            // 同步到store中也重置当前区域
            bridgeMapStore.actions.setCurrentArea(null);
            
            console.log('submitForm保存后本地状态:', {
              selectedArea: this.selectedArea
            });
            console.log('==================================');
          }
        } else {
          // 桥梁信息表单提交
          const success = await this.submitBridgeForm();
          
          if (success) {
            // 返回列表
            setTimeout(() => {
              this.goBack();
            }, 1500);
          }
        }
      } catch (err) {
        console.error('表单提交出错:', err);
        this.$message.error('表单提交出错，请检查输入');
        this.submitting = false;
      }
    },
    
    // 处理窗口大小变化
    handleWindowResize() {
      if (this.map) {
        this.map.invalidateSize();
      }
    },
    
    // 处理区域选择事件
    handleAreaSelected(area, skipEdit) {
      // 为了兼容性，检查是否是数组形式的参数
      if (Array.isArray(area) && area.length >= 1) {
        // 如果是数组，则第一个元素是area，第二个是skipEdit
        skipEdit = area.length > 1 ? area[1] : skipEdit;
        area = area[0];
      }
      
      if (!area) return;
      
      // 使用节流方法防止重复处理
      const now = Date.now();
      if (this._lastSelectedArea === area && 
          this._lastSelectedAreaTime && 
          (now - this._lastSelectedAreaTime < 500)) { // 500ms防抖
        console.log('跳过重复的区域选择事件，间隔:', now - this._lastSelectedAreaTime, 'ms');
        return;
      }
      
      // 记录本次选择的区域和时间
      this._lastSelectedArea = area;
      this._lastSelectedAreaTime = now;
      
      // 【重要修改】使用store统一管理区域选择状态
      if (skipEdit) {
        // 仅选择区域，不进入编辑模式
        const success = bridgeMapStore.actions.setSelectedArea(area);
        if (!success) {
          // 如果因为编辑锁定而无法切换，显示提示
          this.$message.warning('当前正在编辑其他区域，请先保存或取消当前编辑');
          return;
        }
        this.selectedArea = area; // 同步本地状态
      } else {
        // 选择区域并进入编辑模式
        bridgeMapStore.actions.setCurrentArea(area, skipEdit);
        this.selectedArea = area; // 同步本地状态
      }
      
      // 确保当前桥梁ID已设置
      if (this.bridgeForm && this.bridgeForm.id) {
        this.currentBridgeId = this.bridgeForm.id;
        console.log('设置当前桥梁ID:', this.currentBridgeId);
      } else {
        console.warn('处理区域选择时，桥梁ID未设置');
      }
      
      // 如果skipEdit为false，进入编辑模式
      if (!skipEdit) {
        this.editCurrentArea();
      }
    },
    
    // 处理区域面板中的选择事件
    handleAreaSelect(area, skipEdit) {
      if (!area) return;
      
      // 【重要修改】使用store统一管理区域选择
      console.log('区域面板选中区域:', area.name || area.areaName, '，ID:', area.id);
      
      // 使用store的setSelectedArea方法，包含编辑锁定检查
      console.log('handleAreaSelect: 尝试setSelectedArea，当前编辑状态:', {
        isEditingLocked: bridgeMapStore.getters.isEditingLocked(),
        editingAreaId: bridgeMapStore.getters.editingAreaId(),
        skipEdit: skipEdit
      });
      const success = bridgeMapStore.actions.setSelectedArea(area);
      if (!success) {
        // 如果因为编辑锁定而无法切换，显示提示
        console.log('handleAreaSelect: setSelectedArea失败，当前编辑锁定状态');
        this.$message.warning('当前正在编辑其他区域，请先保存或取消当前编辑');
        return;
      }
      
      // 同步本地状态
      this.selectedArea = area;
      
      // 在地图上定位到该区域
      this.selectArea(area);
    },
    
    // 处理区域可见性变更
    handleAreaVisibilityChange(updatedArea) {
      // 更新本地数组中对应区域的可见性
      const areaIndex = this.savedAreas.findIndex(area => area.id === updatedArea.id);
      if (areaIndex !== -1) {
        // 创建数组的副本，避免直接修改原数组
        const updatedAreas = [...this.savedAreas];
        // 更新区域可见性
        updatedAreas[areaIndex] = {
          ...updatedAreas[areaIndex],
          visible: updatedArea.visible
        };
        
        // 更新保存的区域数组
        this.savedAreas = updatedAreas;
        
        // 使用事件总线通知其他组件
        BridgeEventBus.$emit(BRIDGE_EVENTS.AREA_UPDATED, updatedArea);
        
        console.log(`区域 "${updatedArea.name || updatedArea.areaName || '未命名区域'}" 的可见性已设置为 ${updatedArea.visible ? '显示' : '隐藏'}`);
      }
    },
    
    // 处理区域颜色变更
    handleAreaColorChange(color) {
      console.log('区域颜色已变更:', color);
      
      // 更新areaForm中的颜色
      if (this.areaForm) {
        this.areaForm.color = color;
      }
      
      // 通知地图组件更新绘制图形的颜色
      BridgeEventBus.$emit(BRIDGE_EVENTS.AREA_COLOR_CHANGED, {
        color: color,
        areaType: this.areaForm ? this.areaForm.areaType : null
      });
      
      // 如果地图存在，刷新地图以更新视图
      if (this.map) {
        this.$nextTick(() => {
          this.map.invalidateSize();
        });
      }
    },
    
    // 处理几何类型变更
    handleGeometryTypeChange(geometryType) {
      console.log('几何类型已变更:', geometryType);
      
      // 更新areaForm中的几何类型
      if (this.areaForm) {
        this.areaForm.geometryType = geometryType;
      }
      
      // 🎯 同步更新 bridgeMapStore 中的 markType
      let markType = 'polygon'; // 默认值
      switch (geometryType) {
        case 'POINT':
          markType = 'point';
          break;
        case 'LINESTRING':
          markType = 'line';
          break;
        case 'RECTANGLE':
          markType = 'rectangle';
          break;
        default:
          markType = 'polygon';
          break;
      }
      
      // 更新 store 中的标记类型
      bridgeMapStore.actions.setMarkType(markType);
      console.log('已同步更新 bridgeMapStore markType:', markType);
      
      // 🎯 如果当前正在绘制，重新启动绘制模式以应用新的类型
      if (this.isDrawingActive) {
        console.log('当前正在绘制，重新启动绘制以应用新的几何类型');
        // 清除现有绘制内容
        this.clearDrawing();
        // 重新启动绘制
        this.$nextTick(() => {
          bridgeMapStore.actions.startDrawing(true, { markType });
          console.log(`已重新启动绘制，类型: ${markType}`);
        });
      }
      
      // 如果是点标注，确保区域类型为OTHER
      if (geometryType === 'POINT' && this.areaForm && this.areaForm.areaType !== 'OTHER') {
        this.areaForm.areaType = 'OTHER';
        console.log('几何类型为点标注，自动设置区域类型为: OTHER');
      }
    },
    
    // 处理点标注图标变更（已保存的点标注）
    handlePointIconChanged(data) {
      console.log('点标注图标已变更:', data);
      
      if (!data.areaId || !this.$refs.bridgeMap) return;
      
      // 更新BridgeMap组件中已保存的点标注图标
      const bridgeMapComponent = this.$refs.bridgeMap;
      
      // 查找对应的已保存点标注并更新图标
      if (bridgeMapComponent.savedAreas) {
        const area = bridgeMapComponent.savedAreas.find(a => a.id === data.areaId);
        if (area && area.geometryType === 'POINT') {
          area.icon = data.iconName;
          area.iconName = data.iconName;
          console.log('已更新已保存点标注的图标:', data.iconName);
          
          // 强制更新视图
          bridgeMapComponent.$forceUpdate();
        }
      }
    },
    
    // 处理点标注颜色变更（已保存的点标注）
    handlePointColorChanged(data) {
      console.log('点标注颜色已变更:', data);
      
      if (!data.areaId) return;
      
      // 直接通知BridgeMap组件更新颜色，让它统一处理所有颜色更新逻辑
      BridgeEventBus.$emit(BRIDGE_EVENTS.AREA_COLOR_CHANGED, {
        color: data.color,
        areaId: data.areaId,
        areaType: data.areaType
      });
    },
    
    // 处理区域名称变更
    handleAreaNameChanged(data) {
      console.log('区域名称已变更:', data);
      
      if (!data.areaId || !this.$refs.bridgeMap) return;
      
      // 更新BridgeMap组件中已保存区域的名称
      const bridgeMapComponent = this.$refs.bridgeMap;
      
      // 查找对应的已保存区域并更新名称
      if (bridgeMapComponent.savedAreas) {
        const area = bridgeMapComponent.savedAreas.find(a => a.id === data.areaId);
        if (area) {
          area.name = data.name;
          area.areaName = data.name;
          console.log('已更新已保存区域的名称:', data.name);
          
          // 如果是点标注，同时更新点标注的标签
          if (data.geometryType === 'POINT' && bridgeMapComponent.pointMarkers) {
            const pointMarker = bridgeMapComponent.pointMarkers.find(p => 
              p.originalData && p.originalData.id === data.areaId
            );
            if (pointMarker) {
              pointMarker.label = data.name;
              console.log('已更新点标注的标签:', data.name);
            }
          }
          
          // 强制更新视图
          bridgeMapComponent.$forceUpdate();
        }
      }
    },
    
    // 处理位置拾取完成事件 - 使用统一store管理
    handleLocationPicked(latlng) {
      // 使用store处理位置拾取结果
      bridgeMapStore.actions.endLocationPicking(latlng);
      
      // 同步本地状态（以确保视图更新）
      this.bridgeForm.latitude = latlng.lat;
      this.bridgeForm.longitude = latlng.lng;
      this.marker = [latlng.lat, latlng.lng];
      // 不需要在这里设置isLocationPickingActive，使用store中的状态
    },
    
    // 处理工具切换事件
    handleToolChange(tool) {
      console.log('工具切换:', tool);
      // 根据不同工具执行相应操作
      if (tool === 'measure') {
        // 测量工具
        console.log('启用测量工具');
      } else if (tool === 'draw') {
        // 绘制工具
        console.log('启用绘制工具');
        this.startDrawing(true);
      } else if (tool === 'clear') {
        // 清除工具
        console.log('清除绘制');
        this.clearDrawing();
      }
    },
    
    // 从绘制内容创建区域
    createAreaFromDrawing() {
      if (this.drawingCoordinates.length < 3) {
        this.$message.warning('需要至少3个点才能创建区域');
        return;
      }

      // 设置为编辑模式
      this.isEditingArea = true;
      
      // 确保区域类型已设置
      if (!this.areaForm.areaType && this.areaTypeOptions.length > 0) {
        // 使用第一个可用的枚举值
        this.areaForm.areaType = this.areaTypeOptions[0].value;
        console.log('设置默认区域类型枚举值:', this.areaForm.areaType);
      } else {
        console.log('保持现有区域类型:', this.areaForm.areaType);
      }
      
      // 根据区域类型设置区域名称
      if (this.areaForm.areaType && this.areaTypeNameMap[this.areaForm.areaType]) {
        // 通过枚举值找到显示名称并设置区域名称
        const displayName = this.areaTypeNameMap[this.areaForm.areaType];
        this.areaForm.name = displayName;
        this.areaForm.areaName = displayName;
        console.log(`设置区域名称为类型的显示名称: ${displayName} (枚举值: ${this.areaForm.areaType})`);
      } else if (!this.areaForm.areaName && !this.areaForm.name) {
        // 如果没有设置区域名称，使用默认命名
        const defaultName = `区域_${new Date().toLocaleTimeString()}`;
        this.areaForm.name = defaultName;
        this.areaForm.areaName = defaultName;
        console.log('使用默认区域名称:', defaultName);
      } else {
        // 确保name和areaName同步
        if (this.areaForm.name && !this.areaForm.areaName) {
          this.areaForm.areaName = this.areaForm.name;
        } else if (!this.areaForm.name && this.areaForm.areaName) {
          this.areaForm.name = this.areaForm.areaName;
        }
      }
      
      // 确保区域颜色已设置
      if (!this.areaForm.color) {
        this.areaForm.color = mapUtils.getAreaColor(this.areaForm.areaType) || '#3388ff'; // 使用与区域类型匹配的默认颜色
        console.log('设置默认区域颜色:', this.areaForm.color);
      }
      
      // 确保geometryType字段已设置
      if (!this.areaForm.geometryType) {
        this.areaForm.geometryType = 'POLYGON'; // 默认使用多边形
        console.log('设置默认区域几何类型:', this.areaForm.geometryType);
      }
      
      console.log('从绘制内容创建区域:', this.drawingCoordinates.length, '个点');
      console.log('区域表单数据:', JSON.stringify({
        areaName: this.areaForm.name,
        areaType: this.areaForm.areaType,
        color: this.areaForm.color
      }));
    },
    
    // 处理完成绘制
    handleFinishDrawing() {
      if (this.drawingCoordinates.length >= 2) {
        // 关闭绘制模式 - 使用store方法
        bridgeMapStore.actions.startDrawing(false);
        
        // 如果当前未处于编辑模式，则创建区域
        if (!this.isEditingArea) {
          this.createAreaFromDrawing();
        } else {
          // 如果已经在编辑模式，则提示用户保存
          this.$message.info('请点击"保存"按钮保存区域');
        }
      } else {
        this.$message.warning('请至少绘制2个点形成有效线段或区域');
      }
    },
    
    // 处理临时更新区域数据的事件
    handleTempUpdateAreas(updatedAreas) {
      if (Array.isArray(updatedAreas)) {
        console.log('临时更新区域数据，数量:', updatedAreas.length);
        
        // 临时更新savedAreas数组
        this.savedAreas = [...updatedAreas];
        
                  // 确保视图更新
          this.$nextTick(() => {
            console.log('区域数据已更新，无需重复发送事件');
            // 已由BridgeAreaController的loadBridgeAreas方法发送事件，此处不再重复发送
            // BridgeEventBus.$emit(BRIDGE_EVENTS.AREA_DATA_LOADED, this.savedAreas);
          });
      }
    },
    
    // 处理保存编辑后的区域
    handleSaveEditedArea(coordinates) {
      if (!this.selectedArea || !this.selectedArea.id) {
        this.$message.warning('未选择区域，无法保存编辑');
        return;
      }
      
      // 检查坐标点是否足够
      if (!coordinates || coordinates.length < 3) {
        this.$message.warning('区域需要至少3个点才能保存');
        return;
      }
      
      console.log('保存编辑后的区域:', this.selectedArea.id);
      
      // 更新区域表单数据
      this.areaForm.id = this.selectedArea.id;
      this.areaForm.areaName = this.selectedArea.areaName || this.selectedArea.name;
      this.areaForm.areaType = this.selectedArea.areaType;
      this.areaForm.bridgeId = this.selectedArea.bridgeId || this.bridgeForm.id;
      
      // 更新坐标数据
      this.drawingCoordinates = coordinates;
      
      // 保存区域
      this.saveArea().then(success => {
        if (success) {
          this.$message.success('区域编辑成功保存');
          
          // 【调试】保存成功后的状态清理诊断
          console.log('=== 保存成功，开始状态清理 ===');
          console.log('保存前store状态:', {
            isEditingLocked: bridgeMapStore.getters.isEditingLocked(),
            editingAreaId: bridgeMapStore.getters.editingAreaId(),
            isEditingArea: bridgeMapStore.getters.isEditingArea()
          });
          
          // 【重要修改】使用store统一管理编辑结束
          bridgeMapStore.actions.endEditingArea();
          
          // 【修复】通知AreaPanel组件保存成功，同步清理状态
          if (this.$refs.areaPanel && typeof this.$refs.areaPanel.handleSaveSuccess === 'function') {
            this.$refs.areaPanel.handleSaveSuccess();
          }
          
          // 同步本地编辑状态
          this.isEditingArea = false;
          this.selectedArea = null;
          
          console.log('保存后本地状态:', {
            isEditingArea: this.isEditingArea,
            selectedArea: this.selectedArea
          });
          console.log('保存后store状态:', {
            isEditingLocked: bridgeMapStore.getters.isEditingLocked(),
            editingAreaId: bridgeMapStore.getters.editingAreaId(),
            isEditingArea: bridgeMapStore.getters.isEditingArea()
          });
          console.log('===============================');
          
          // 获取当前桥梁ID
          const bridgeId = this.bridgeForm.id || this.currentBridgeId;
          
          // 重新加载区域数据，显示更新后的区域
          if (bridgeId) {
            console.log('重新加载区域数据，桥梁ID:', bridgeId);
            this.loadBridgeAreas(bridgeId);
          }
          
          // 更新图层显示
          if (this.map) {
            this.$nextTick(() => {
              this.map.invalidateSize();
              console.log('地图视图已更新');
            });
          }
        } else {
          this.$message.error('区域保存失败');
        }
      });
    },
    
    // 处理取消编辑区域
    handleCancelEditing() {
      console.log('取消区域编辑');
      
      // 【重要修改】使用store统一管理取消编辑
      bridgeMapStore.actions.cancelEditingArea();
      
      // 获取当前桥梁ID
      const bridgeId = this.bridgeForm.id || this.currentBridgeId;
      
      // 保存当前选中区域的引用，以便后续恢复
      const currentArea = this.selectedArea;
      
      // 同步本地编辑状态
      this.isEditingArea = false;
      
      // 确保清除BridgeMap组件中的点标注
      if (this.$refs.bridgeMap && typeof this.$refs.bridgeMap.clearDrawing === 'function') {
        console.log('清除桥梁地图组件中的点标注');
        this.$refs.bridgeMap.clearDrawing();
      }
      
      // 重新加载区域数据，恢复显示原区域
      if (bridgeId) {
        console.log('重新加载区域数据，桥梁ID:', bridgeId);
        this.loadBridgeAreas(bridgeId);
        
        // 确保重新加载后恢复选中的区域
        this.$nextTick(() => {
          if (currentArea && currentArea.id) {
            // 找回对应的区域
            const foundArea = this.savedAreas.find(a => a.id === currentArea.id);
            if (foundArea) {
              console.log('恢复选中区域:', foundArea.id, foundArea.name || foundArea.areaName);
              this.selectedArea = foundArea;
            }
          }
        });
      }
      
      // 更新图层显示
      if (this.map) {
        this.$nextTick(() => {
          this.map.invalidateSize();
          console.log('地图视图已更新');
        });
      }
    },
    
    // 清除绘制内容
    clearDrawing() {
      // 先备份坐标数组长度，用于判断是否真的需要刷新地图
      const hadCoordinates = this.drawingCoordinates.length > 0;
      
      // 使用store方法清空绘制内容
      bridgeMapStore.actions.clearDrawing();
      
      // 确保清除BridgeMap组件中的点标注
      if (this.$refs.bridgeMap && typeof this.$refs.bridgeMap.clearDrawing === 'function') {
        console.log('清除桥梁地图组件中的点标注');
        this.$refs.bridgeMap.clearDrawing();
      }
      
      // 如果之前有坐标，且地图实例存在，则刷新地图
      if (hadCoordinates && this.map) {
        setTimeout(() => {
          try {
            this.map.invalidateSize({animate: false});
          } catch (e) {
            console.warn('地图尺寸更新失败，但不影响使用', e);
          }
        }, 100);
      }
    },
    
    // 清除当前区域 - 使用store中的方法
    clearCurrentArea() {
      if (this.isEditingArea && this.selectedArea) {
        // 使用确认框防止误操作
        this.$confirm('确定要清除当前区域的绘制内容吗？清除后可重新绘制。', '提示', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        }).then(() => {
          // 清空坐标数组
          this.drawingCoordinates = [];
          
          // 调用store的方法
          bridgeMapStore.actions.clearCurrentArea();
          
          // 提示用户
          this.$message({
            type: 'success',
            message: '区域已清除，可以重新绘制'
          });
          
          // 保持编辑状态
          this.isEditingArea = true;
          
          // 通知地图组件更新视图
          if (this.map) {
            // 刷新图层
            this.$nextTick(() => {
              this.map.invalidateSize();
              // 注意：由于优化事件处理，此处不再发送区域数据更新事件
              // 由bridgeMapStore的clearCurrentArea方法已经发送了AREA_CLEARED事件
              console.log('地图图层已刷新，无需额外发送事件');
            });
          }
        }).catch(() => {
          // 用户取消操作
        });
      } else {
        this.$message({
          type: 'warning',
          message: '当前没有选中的区域'
        });
      }
    },
    
    // 重绘当前区域
    redrawCurrentArea() {
      if (this.isEditingArea && this.selectedArea) {
        console.log('开始重绘当前区域，区域ID:', this.selectedArea.id);
        
        // 记录当前区域
        const currentArea = { ...this.selectedArea };
        
        // 先清除当前绘制内容
        this.drawingCoordinates = [];
        
        // 从已保存的区域中临时移除当前区域
        if (Array.isArray(this.savedAreas) && this.selectedArea.id) {
          console.log('临时从savedAreas中移除区域:', this.selectedArea.id);
          
          // 创建一个新数组，排除当前区域
          const updatedAreas = this.savedAreas.filter(area => area.id !== this.selectedArea.id);
          
          // 更新savedAreas数组以触发视图更新
          this.savedAreas = [...updatedAreas];
          
          console.log('更新后的savedAreas长度:', this.savedAreas.length);
        }
        
        // 启动绘制模式
        this.startDrawing(true);
        
        // 提示用户
        this.$message({
          type: 'info',
          message: '请开始绘制新的区域轮廓'
        });
        
        // 更新图层显示
        if (this.map) {
          console.log('强制刷新地图视图');
          this.$nextTick(() => {
            this.map.invalidateSize();
            
            // 注意：由于优化事件处理，此处不再发送区域数据更新事件
            // 避免重复触发事件导致性能问题
            console.log('地图视图已更新，无需重复发送事件');
          });
        }
      } else {
        this.$message({
          type: 'warning',
          message: '当前没有选中的区域'
        });
      }
    },
    
    // 处理临时更新区域数据的事件
    handleTempUpdateAreas(updatedAreas) {
      if (Array.isArray(updatedAreas)) {
        console.log('临时更新区域数据，数量:', updatedAreas.length);
        
        // 临时更新savedAreas数组
        this.savedAreas = [...updatedAreas];
        
                  // 确保视图更新
          this.$nextTick(() => {
            console.log('区域数据已更新，无需重复发送事件');
            // 已由BridgeAreaController的loadBridgeAreas方法发送事件，此处不再重复发送
            // BridgeEventBus.$emit(BRIDGE_EVENTS.AREA_DATA_LOADED, this.savedAreas);
          });
      }
    },
    
    // 编辑当前区域（显示空心圆可拖拽状态）
    editCurrentArea(area) {
      console.log('editCurrentArea 被调用，传入区域:', area);
      
      // 如果传入了区域参数，使用传入的区域
      let areaToEdit = area;
      
      // 如果没有传入区域，检查是否有选中的区域
      if (!areaToEdit && !this.selectedArea) {
        console.warn('编辑当前区域失败：没有传入区域参数且selectedArea为空', this.selectedArea);
        // 如果没有选中的区域，提示用户
        this.$message({
          type: 'warning',
          message: '请先从区域列表中选择一个区域'
        });
        return;
      }
      
      // 如果没有传入区域，使用当前选中的区域
      if (!areaToEdit) {
        areaToEdit = this.selectedArea;
      }
      
      // 同步 selectedArea 状态
      if (areaToEdit && areaToEdit !== this.selectedArea) {
        this.selectedArea = areaToEdit;
        console.log('已同步 selectedArea:', areaToEdit.name || areaToEdit.areaName);
      }
      
      // 【重要修改】使用store的编辑锁定机制
      // 【调试】添加详细的诊断信息
      console.log('=== 开始编辑区域诊断 ===');
      console.log('要编辑的区域:', areaToEdit);
      console.log('当前selectedArea:', this.selectedArea);
      console.log('store当前状态:', {
        isEditingLocked: bridgeMapStore.getters.isEditingLocked(),
        editingAreaId: bridgeMapStore.getters.editingAreaId(),
        isEditingArea: bridgeMapStore.getters.isEditingArea()
      });
      
      const success = bridgeMapStore.actions.startEditingArea(areaToEdit);
      console.log('startEditingArea结果:', success);
      
      if (!success) {
        console.log('=== 编辑失败，当前store状态 ===');
        console.log('isEditingLocked:', bridgeMapStore.getters.isEditingLocked());
        console.log('editingAreaId:', bridgeMapStore.getters.editingAreaId());
        console.log('传入的区域ID:', areaToEdit?.id);
        console.log('===========================');
        this.$message.warning('无法开始编辑该区域，请检查区域状态');
        return;
      }
      
      // 检查是否已经在编辑状态
      if (!this.isEditingArea) {
        // 如果不在编辑状态，先切换到编辑状态
        this.isEditingArea = true;
        
        // 使用要编辑的区域初始化编辑表单
        this.areaForm = { ...areaToEdit };
        
        // 确保name和areaName字段都存在
        if (this.areaForm.name && !this.areaForm.areaName) {
          this.areaForm.areaName = this.areaForm.name;
        } else if (!this.areaForm.name && this.areaForm.areaName) {
          this.areaForm.name = this.areaForm.areaName;
        }
        
        // 确保color字段存在
        if (!this.areaForm.color) {
          // this.areaForm.color = '#3388ff'; // 使用默认颜色
          this.areaForm.color = bridgeMapStore.getters.getColorByAreaId(areaToEdit.id);
        }
        
        // 确保geometryType字段存在
        if (!this.areaForm.geometryType) {
          this.areaForm.geometryType = 'POLYGON'; // 默认使用多边形
        }
        
        console.log('开始编辑区域:', areaToEdit.id);
        
        // 【修复】设置编辑状态后，立即进入地图编辑模式
        this.$nextTick(() => {
          // 调用自身来执行地图编辑逻辑（此时 isEditingArea 已为 true）
          this.enterMapEditMode(areaToEdit);
        });
        return;
      }
      
      // 开始编辑坐标点（已经在编辑状态且有选中区域时）
      console.log('编辑当前区域，区域ID:', areaToEdit.id);
      this.enterMapEditMode(areaToEdit);
    },
    
    // 处理地图区域点击编辑事件（与列表编辑行为一致）
    editArea(area) {
      console.log('BridgeManagement.editArea 被调用，区域:', area);
      
      // 【修复】特殊处理新建区域的情况，直接调用BridgeAreaController的实现
      if (area && area.isNew === true) {
        console.log('检测到新建区域请求，调用BridgeAreaController的editArea方法');
        // 调用原始的BridgeAreaController.editArea方法
        return BridgeAreaController.methods.editArea.call(this, area);
      }
      
      // 对于现有区域的编辑，使用editCurrentArea
      console.log('现有区域编辑，调用editCurrentArea');
      this.editCurrentArea(area);
    },
    
    // 进入地图编辑模式
    enterMapEditMode(area) {
      // 获取当前选中的区域
      const currentArea = { ...area };
      
      try {
        // 解析当前区域坐标
        const coordinates = mapUtils.parseAreaCoordinates(currentArea);
        
        if (!coordinates) {
          this.$message.warning('区域坐标无效，无法编辑');
          return;
        }
        
        // 从已保存的区域中临时移除当前区域，避免显示重叠
        // 这一步是关键，否则在拖拽空心圆时底下会显示原区域的多边形
        if (Array.isArray(this.savedAreas) && area.id) {
          console.log('临时从savedAreas中移除区域:', area.id);
          
          // 创建一个新数组，排除当前区域
          const updatedAreas = this.savedAreas.filter(a => a.id !== area.id);
          
          // 更新savedAreas数组以触发视图更新
          this.savedAreas = [...updatedAreas];
          
          console.log('更新后的savedAreas长度:', this.savedAreas.length);
        }
        
        // 设置绘制坐标
        this.drawingCoordinates = coordinates;
        
        // 启用空心圆可拖拽模式
        this.$nextTick(() => {
          if (this.$refs.bridgeMap) {
            // 调用BridgeMap组件的enterPointEditMode方法
            this.$refs.bridgeMap.enterPointEditMode(coordinates);
            this.$message.success('已进入编辑模式，您可以拖拽点调整区域形状');
            
            // 更新图层显示
            if (this.map) {
              console.log('强制刷新地图视图');
              this.map.invalidateSize();
              console.log('地图视图已更新');
            }
          }
        });
      } catch (error) {
        console.error('解析区域坐标失败:', error);
        this.$message.error('无法编辑区域坐标');
      }
    },
    
    // 开始绘制区域
    startDrawing(active, options) {
      // 支持直接传递对象参数
      if (typeof active === 'object' && active !== null) {
        options = active;
        active = options.active;
      }

      // 确保激活状态为true
      active = active !== false;
      
      // 本地更新状态
      this.isDrawingActive = active;
      
      const { markType, iconName } = options || {};
      
      // 添加详细日志
      console.log('开始绘制:', {
        active: active,
        markType: markType,
        iconName: iconName,
        areaType: this.areaForm ? this.areaForm.areaType : null,
        geometryType: this.areaForm ? this.areaForm.geometryType : null
      });
      
      // 检查是否传入markType参数
      if (markType) {
        // 如果是点标注，确保geometryType也设置为POINT
        if (markType === 'point' && this.areaForm) {
          this.areaForm.geometryType = 'POINT';
          
          // 如果传入了图标名称，也更新到表单中
          if (iconName) {
            this.areaForm.iconName = iconName;
          }
        }
        
        // 传递markType和iconName到store方法
        bridgeMapStore.actions.startDrawing(active, { markType, iconName });
        console.log(`启动${markType}标注绘制模式${iconName ? ', 图标: ' + iconName : ''}`);
      } else {
        // 兼容旧的调用方式
        bridgeMapStore.actions.startDrawing(active);
      }
    },
    
    // 🎯 根据几何类型获取对应的标记类型
    getMarkTypeFromGeometryType(geometryType) {
      const mapping = {
        'POINT': 'point',
        'LINESTRING': 'line',
        'RECTANGLE': 'rectangle',
        'CIRCLE': 'circle',
        'POLYGON': 'polygon'
      };
      return mapping[geometryType] || 'polygon';
    },
  }
}; 